/**
 * FollowUpCard – suggested actions rendered after a task reaches a terminal
 * state (COMPLETED, FAILED, CANCELLED).
 *
 * Shows a short list of canned follow-up prompts derived from the task goal
 * so the user can continue the conversation or spawn a related task without
 * typing from scratch.
 */
import React, { useState } from 'react';
import { Button, Card, Input, Space, Tag, Typography } from 'antd';
import {
  PlusOutlined, SearchOutlined, FileTextOutlined,
  QuestionCircleOutlined, SendOutlined,
} from '@ant-design/icons';

const { Text, Title } = Typography;
const { TextArea } = Input;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FollowUpSuggestion {
  label: string;
  prompt: string;
  icon?: React.ReactNode;
  tag?: string;
}

interface Props {
  taskId: string;
  taskGoal: string;
  taskStatus: string;
  onSubmit: (message: string) => void;
  submitting?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildSuggestions(goal: string): FollowUpSuggestion[] {
  const trimmed = goal.slice(0, 80);
  return [
    {
      label: 'Summarise findings',
      prompt: `Summarise the key findings from "${trimmed}" in bullet points`,
      icon: <FileTextOutlined />,
      tag: 'document',
    },
    {
      label: 'Dig deeper',
      prompt: `Research further into the most important aspect of "${trimmed}"`,
      icon: <SearchOutlined />,
      tag: 'research',
    },
    {
      label: 'Ask a question',
      prompt: `What are the next steps I should take based on the results of "${trimmed}"?`,
      icon: <QuestionCircleOutlined />,
      tag: 'question',
    },
    {
      label: 'Create an action plan',
      prompt: `Create a detailed action plan based on the results of "${trimmed}"`,
      icon: <PlusOutlined />,
      tag: 'action',
    },
  ];
}

// ── Component ─────────────────────────────────────────────────────────────────

const FollowUpCard: React.FC<Props> = ({
  taskId: _taskId,
  taskGoal,
  taskStatus,
  onSubmit,
  submitting = false,
}) => {
  const [customText, setCustomText] = useState('');
  const suggestions = buildSuggestions(taskGoal);

  const isTerminal = ['COMPLETED', 'FAILED', 'CANCELLED'].includes(taskStatus.toUpperCase());
  if (!isTerminal) return null;

  const handleSend = () => {
    const text = customText.trim();
    if (!text) return;
    onSubmit(text);
    setCustomText('');
  };

  return (
    <Card
      size="small"
      style={{
        background: '#1c2128',
        border: '1px solid #2d333b',
        borderRadius: 10,
        marginTop: 8,
      }}
      bodyStyle={{ padding: '12px 14px' }}
    >
      <Title level={5} style={{ color: '#e6edf3', fontSize: 13, marginBottom: 10 }}>
        What would you like to do next?
      </Title>

      {/* Canned suggestions */}
      <Space direction="vertical" size={6} style={{ width: '100%', marginBottom: 12 }}>
        {suggestions.map(s => (
          <button
            key={s.label}
            type="button"
            onClick={() => onSubmit(s.prompt)}
            disabled={submitting}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              background: '#0d1117',
              border: '1px solid #2d333b',
              borderRadius: 7,
              padding: '7px 10px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              textAlign: 'left',
              color: '#c9d1d9',
              fontSize: 12,
              transition: 'border-color 0.15s, background 0.15s',
            }}
            onMouseEnter={e => {
              if (!submitting) {
                (e.currentTarget as HTMLButtonElement).style.borderColor = '#3b82f6';
                (e.currentTarget as HTMLButtonElement).style.background = '#161b22';
              }
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = '#2d333b';
              (e.currentTarget as HTMLButtonElement).style.background = '#0d1117';
            }}
          >
            <span style={{ color: '#8b949e', fontSize: 13 }}>{s.icon}</span>
            <span style={{ flex: 1 }}>{s.label}</span>
            {s.tag && (
              <Tag style={{ fontSize: 10, margin: 0 }}>{s.tag}</Tag>
            )}
          </button>
        ))}
      </Space>

      {/* Custom input */}
      <div style={{ display: 'flex', gap: 6 }}>
        <TextArea
          value={customText}
          onChange={e => setCustomText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
          }}
          placeholder="Or type a custom follow-up…"
          autoSize={{ minRows: 1, maxRows: 3 }}
          disabled={submitting}
          style={{
            flex: 1,
            background: '#0d1117',
            border: '1px solid #2d333b',
            color: '#c9d1d9',
            fontSize: 12,
            borderRadius: 7,
            resize: 'none',
          }}
        />
        <Button
          type="primary"
          size="small"
          icon={<SendOutlined />}
          onClick={handleSend}
          disabled={submitting || !customText.trim()}
          loading={submitting}
          style={{ alignSelf: 'flex-end' }}
        />
      </div>

      <Text type="secondary" style={{ fontSize: 10, marginTop: 8, display: 'block' }}>
        Replies will continue in this session. Complex actions launch a new task.
      </Text>
    </Card>
  );
};

export default FollowUpCard;
